// Regalo junto al equipo (REQ-11): opciones, preselección con una sola y bloqueo con varias.
import type {
    EvaluateSalePromotionsResponse,
    RetailPromotionsErrorCode,
} from '../api/wizardPromotionsContract';
import { forAnchor, isSureKeepOnly } from './promotionAppliesTo';
import type { PromotionChoice } from './promotionSelection';

export interface GiftPanelOption {
    promotionId: string;
    promotionVersion: number;
    sku: string;
    name: string;
    /** Valor del regalo: su precio de venta. */
    valueCents: number;
    inStock: boolean;
    /** La promo de regalo solo aplica con crédito SureKeep. */
    sureKeepOnly: boolean;
}

export interface BlockedPromotion {
    promotionId: string;
    name: string;
    blockedBy: RetailPromotionsErrorCode;
}

export interface GiftPanel {
    options: GiftPanelOption[];
    blocked: BlockedPromotion[];
    /** Única opción con existencia, cuando el vendedor aún no decide. */
    autoChoice?: PromotionChoice;
    /** Hay varias opciones y falta elegir una o «Sin regalo»: bloquea «Continuar». */
    needsDecision: boolean;
}

/** Con `withoutSureKeep` (SureKeep no financia el equipo) quedan fuera las promos solo SureKeep. */
/** Valor del regalo; un promotions sin desplegar solo manda `listPriceCents` (sale en el cierre 5a). */
export function giftValueCentsOf(option: { valueCents?: number; listPriceCents?: number }): number {
    return option.valueCents ?? option.listPriceCents ?? 0;
}

export function giftPanelOf(
    evaluation: EvaluateSalePromotionsResponse,
    /** Regalo del teléfono ya elegido; `undefined` = sin decidir. */
    current: PromotionChoice | null | undefined,
    withoutSureKeep = false,
): GiftPanel {
    const giftPromos = forAnchor(
        evaluation.promotions.filter((p) => p.kind === 'GIFT'),
        withoutSureKeep,
    );
    const blocked: BlockedPromotion[] = [];
    const options: GiftPanelOption[] = [];
    for (const promo of giftPromos) {
        if (promo.blockedBy) {
            blocked.push({
                promotionId: promo.promotionId,
                name: promo.name,
                blockedBy: promo.blockedBy,
            });
            continue;
        }
        for (const option of promo.giftOptions) {
            options.push({
                ...option,
                valueCents: giftValueCentsOf(option),
                promotionId: promo.promotionId,
                promotionVersion: promo.version,
                sureKeepOnly: isSureKeepOnly(promo),
            });
        }
    }
    const available = options.filter((o) => o.inStock);
    const undecided = current === undefined;
    const only = available.length === 1 ? available[0] : undefined;
    return {
        options,
        blocked,
        autoChoice:
            undecided && only
                ? {
                      promotionId: only.promotionId,
                      promotionVersion: only.promotionVersion,
                      giftSkus: [only.sku],
                  }
                : undefined,
        needsDecision: undecided && available.length > 1,
    };
}

/** Nombre de un regalo según las opciones de cualquier promo evaluada (de regalo o de venta). */
export function giftNameOf(
    evaluation: EvaluateSalePromotionsResponse | undefined,
    sku: string,
): string | undefined {
    for (const promo of evaluation?.promotions ?? []) {
        const option = promo.giftOptions.find((o) => o.sku === sku);
        if (option) return option.name;
    }
    return undefined;
}

/** Nombre de una promo evaluada: prefiere la versión que tomó la línea, si no la misma promo. */
export function evaluatedPromotionName(
    evaluation: EvaluateSalePromotionsResponse | undefined,
    promotionId: string,
    promotionVersion?: number,
): string | undefined {
    const same = (evaluation?.promotions ?? []).filter((p) => p.promotionId === promotionId);
    const exact = same.find((p) => p.version === promotionVersion);
    return exact !== undefined ? exact.name : same.at(0)?.name;
}
